import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { 
  Star, 
  Send, 
  ArrowLeft,
  CheckCircle
} from 'lucide-react';
import { creditsService, FeedbackSubmission } from '../../services/creditsService';

interface FeedbackFormProps {
  onSubmit: () => void;
  onCancel: () => void;
}

interface FormData {
  usageFrequency: string;
  primaryUseCase: string;
  overallSatisfaction: number;
  easeOfUse: number;
  outputQuality: number;
  importantFeatures: string[];
  newFeatures: string;
  painPoints: string;
  npsScore: number;
  upgradeMotivation: string;
  additionalComments: string;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<FormData>({
    usageFrequency: '',
    primaryUseCase: '',
    overallSatisfaction: 0,
    easeOfUse: 0,
    outputQuality: 0,
    importantFeatures: [],
    newFeatures: '',
    painPoints: '',
    npsScore: 0,
    upgradeMotivation: '',
    additionalComments: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const totalSteps = 3;

  const usageOptions = [
    'Daily',
    'Weekly', 
    'Monthly',
    'Rarely'
  ];

  const useCaseOptions = [
    'Marketing',
    'Content Creation',
    'Social Media',
    'Business',
    'Personal',
    'Other'
  ];

  const featureOptions = [
    'AI Content Generation',
    'Social Media Scheduling',
    'Analytics & Insights',
    'Team Collaboration',
    'Template Library',
    'Multi-platform Support',
    'Custom Branding',
    'API Access'
  ];

  const upgradeOptions = [
    'More credits/usage',
    'Advanced AI features',
    'Better analytics',
    'Priority support',
    'Team features',
    'Custom integrations',
    'White-label options',
    'Other'
  ];

  const StarRating = ({ 
    value, 
    onChange, 
    label 
  }: { 
    value: number; 
    onChange: (rating: number) => void; 
    label: string;
  }) => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <div className="flex space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className="transition-colors"
          >
            <Star 
              className={`w-6 h-6 ${
                star <= value 
                  ? 'text-yellow-400 fill-yellow-400' 
                  : 'text-gray-300 dark:text-gray-600'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );

  const NPSScale = ({ 
    value, 
    onChange 
  }: { 
    value: number; 
    onChange: (score: number) => void; 
  }) => (
    <div className="space-y-2">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        How likely are you to recommend us? (0-10)
      </label>
      <div className="grid grid-cols-11 gap-1">
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onChange(i)}
            className={`
              h-8 text-xs font-medium rounded transition-colors
              ${value === i 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }
            `}
          >
            {i}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-xs text-gray-500">
        <span>Not likely</span>
        <span>Very likely</span>
      </div>
    </div>
  );

  const handleFeatureToggle = (feature: string) => {
    setFormData(prev => ({
      ...prev,
      importantFeatures: prev.importantFeatures.includes(feature)
        ? prev.importantFeatures.filter(f => f !== feature)
        : [...prev.importantFeatures, feature]
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const feedbackData: FeedbackSubmission = {
        usage_frequency: formData.usageFrequency,
        primary_use_case: formData.primaryUseCase,
        overall_satisfaction: formData.overallSatisfaction,
        ease_of_use: formData.easeOfUse,
        output_quality: formData.outputQuality,
        important_features: formData.importantFeatures,
        new_features: formData.newFeatures,
        pain_points: formData.painPoints,
        nps_score: formData.npsScore,
        upgrade_motivation: formData.upgradeMotivation,
        additional_comments: formData.additionalComments
      };

      const response = await creditsService.submitFeedback(feedbackData);
      console.log('Feedback submitted successfully:', response);
      
      setIsSubmitting(false);
      onSubmit();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      setIsSubmitting(false);
      // You might want to show an error message to the user here
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Usage & Experience
            </h3>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  How often do you use our service?
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {usageOptions.map(option => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, usageFrequency: option }))}
                      className={`
                        p-3 text-sm rounded-lg border transition-colors
                        ${formData.usageFrequency === option
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }
                      `}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  What's your primary use case?
                </label>
                <select
                  value={formData.primaryUseCase}
                  onChange={(e) => setFormData(prev => ({ ...prev, primaryUseCase: e.target.value }))}
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="">Select an option</option>
                  {useCaseOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <StarRating
                value={formData.overallSatisfaction}
                onChange={(rating) => setFormData(prev => ({ ...prev, overallSatisfaction: rating }))}
                label="Rate overall satisfaction"
              />

              <StarRating
                value={formData.easeOfUse}
                onChange={(rating) => setFormData(prev => ({ ...prev, easeOfUse: rating }))}
                label="Rate ease of use"
              />

              <StarRating
                value={formData.outputQuality}
                onChange={(rating) => setFormData(prev => ({ ...prev, outputQuality: rating }))}
                label="Rate output quality"
              />
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Feature Priorities
            </h3>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">
                  Which features are most important to you? (Select multiple)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {featureOptions.map(feature => (
                    <button
                      key={feature}
                      type="button"
                      onClick={() => handleFeatureToggle(feature)}
                      className={`
                        p-3 text-sm rounded-lg border transition-colors text-left
                        ${formData.importantFeatures.includes(feature)
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300'
                          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }
                      `}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={`
                          w-4 h-4 rounded border-2 flex items-center justify-center
                          ${formData.importantFeatures.includes(feature)
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-gray-300 dark:border-gray-600'
                          }
                        `}>
                          {formData.importantFeatures.includes(feature) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <span>{feature}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  What new features would you like to see?
                </label>
                <textarea
                  value={formData.newFeatures}
                  onChange={(e) => setFormData(prev => ({ ...prev, newFeatures: e.target.value }))}
                  placeholder="Describe any features you'd like us to add..."
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none"
                  rows={3}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  What's your biggest pain point?
                </label>
                <textarea
                  value={formData.painPoints}
                  onChange={(e) => setFormData(prev => ({ ...prev, painPoints: e.target.value }))}
                  placeholder="Tell us about any challenges you face..."
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none"
                  rows={3}
                />
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Service Improvement
            </h3>

            <div className="space-y-4">
              <NPSScale
                value={formData.npsScore}
                onChange={(score) => setFormData(prev => ({ ...prev, npsScore: score }))}
              />

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  What would make you upgrade to a higher tier?
                </label>
                <select
                  value={formData.upgradeMotivation}
                  onChange={(e) => setFormData(prev => ({ ...prev, upgradeMotivation: e.target.value }))}
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="">Select an option</option>
                  {upgradeOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                  Additional comments
                </label>
                <textarea
                  value={formData.additionalComments}
                  onChange={(e) => setFormData(prev => ({ ...prev, additionalComments: e.target.value }))}
                  placeholder="Any other feedback or suggestions..."
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white resize-none"
                  rows={4}
                />
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div className="flex items-center space-x-2">
        <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-green-500 to-emerald-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {currentStep} of {totalSteps}
        </span>
      </div>

      {/* Form Content */}
      {renderStep()}

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="outline"
          onClick={currentStep === 1 ? onCancel : () => setCurrentStep(prev => prev - 1)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {currentStep === 1 ? 'Cancel' : 'Back'}
        </Button>

        {currentStep < totalSteps ? (
          <Button
            onClick={() => setCurrentStep(prev => prev + 1)}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit & Get 200 Credits
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default FeedbackForm;